import React, { useState } from "react";
import styled from "styled-components";
import Upload, { UploadFile } from "antd/es/upload";
import { FileSpreadsheet } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import Modal from "common/Modal/Modal.tsx";
import Select from "common/Select/Select.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { displayResultMessage, openMessage } from "utils/misc.tsx";
import { useUploadFinanceStatementMutation } from "app/services/finance.service";
import { FinanceCurrency, FinanceSource } from "features/finance/types";
import {
  FINANCE_CURRENCIES,
  SOURCE_TO_LABEL,
  STATEMENT_FILE_ACCEPT,
  STATEMENT_SOURCES,
} from "features/finance/constants";
import {
  Field,
  FieldHint,
  FieldInline,
  FieldLabel,
  Fields,
} from "features/finance/formStyles";

interface UploadStatementModalProps {
  open: boolean;
  onClose: () => void;
}

const UploadStatementModal = ({ open, onClose }: UploadStatementModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const [source, setSource] = useState<FinanceSource>(FinanceSource.ISRACARD);
  const [currency, setCurrency] = useState<FinanceCurrency>(
    FinanceCurrency.ILS
  );
  const [file, setFile] = useState<UploadFile>();
  const [showErrors, setShowErrors] = useState(false);
  const [uploadStatement, { isLoading }] = useUploadFinanceStatementMutation();

  const close = () => {
    setFile(undefined);
    setShowErrors(false);
    onClose();
  };

  const onSubmit = () => {
    const rawFile = file?.originFileObj;
    if (!file || !rawFile) {
      setShowErrors(true);
      return;
    }
    displayResultMessage(
      uploadStatement({ source, currency, file: rawFile }).unwrap(),
      undefined,
      t("finance.upload.failed"),
      undefined,
      undefined,
      undefined,
      false
    )
      .then((response) => {
        openMessage(
          t("finance.upload.success", { count: response.saved_count }),
          "success"
        );
        close();
      })
      .catch(() => {});
  };

  return (
    <Modal
      open={open}
      title={t("finance.upload.title")}
      onOk={onSubmit}
      okText={t("finance.upload.submit")}
      okButtonProps={{ loading: isLoading }}
      onCancel={close}
      destroyOnHidden={true}
    >
      <Fields>
        <Field>
          <FieldLabel>{t("finance.upload.source")}</FieldLabel>
          <Select
            value={source}
            options={STATEMENT_SOURCES.map((value) => ({
              value,
              label: t(SOURCE_TO_LABEL[value]),
            }))}
            onChange={setSource}
            mobileTitle={t("finance.upload.source")}
            style={{ width: "100%" }}
          />
        </Field>
        <Field>
          <FieldLabel>{t("finance.upload.currency")}</FieldLabel>
          <FieldInline>
            <SegmentTabs
              options={FINANCE_CURRENCIES}
              value={currency}
              onChange={setCurrency}
              ariaLabel={t("finance.upload.currency")}
            />
          </FieldInline>
          <FieldHint>{t("finance.upload.currency_hint")}</FieldHint>
        </Field>
        <Field>
          <FieldLabel>{t("finance.upload.file")}</FieldLabel>
          <Upload.Dragger
            accept={STATEMENT_FILE_ACCEPT}
            maxCount={1}
            multiple={false}
            fileList={file ? [file] : []}
            beforeUpload={() => false}
            onChange={({ fileList }) => setFile(fileList[fileList.length - 1])}
            onRemove={() => setFile(undefined)}
          >
            <DropContent>
              <FileSpreadsheet size={24} strokeWidth={1.5} />
              <span>{t("finance.upload.file_hint")}</span>
            </DropContent>
          </Upload.Dragger>
          <RequiredFieldMessage
            requirement={showErrors && !file}
            errorMessage={t("finance.upload.file_required")}
          />
        </Field>
      </Fields>
    </Modal>
  );
};

export default UploadStatementModal;

const DropContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
